import React, { useState } from 'react';

// ── Label popover ─────────────────────────────────────────────────────────────
// Tapping a truncated header shows the full text in an overlay.
// Works on both mobile (tap) and desktop (click).
function LabelPopover({ tag, label, accentClass, height }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={`bg-surface2 border border-border rounded-xl p-2.5 text-center text-[11px] font-semibold text-dimText flex flex-col items-center justify-center gap-1 ${height} leading-snug overflow-hidden cursor-pointer active:bg-surface3 transition-colors`}
      >
        <span className={`text-[9px] font-mono tracking-widest ${accentClass} opacity-70 uppercase truncate w-full text-center`}>{tag}</span>
        <span style={{display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}} className="w-full">{label}</span>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          onClick={() => setOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          {/* Card */}
          <div
            className="relative bg-surface2 border border-accent/40 rounded-2xl p-5 max-w-xs w-full shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className={`text-[10px] font-mono tracking-widest ${accentClass} opacity-70 uppercase mb-2`}>{tag}</div>
            <p className="text-sm font-semibold text-white leading-snug">{label}</p>
            <button
              onClick={() => setOpen(false)}
              className="mt-4 w-full text-xs text-dimText border border-border rounded-lg py-2 hover:border-accent hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function Grid({ puzzle, game, revealed = false, focusSearch }) {
  const { grid, hintShown, activeCell, selectCell } = game;

  const getCell = (r, c) => puzzle.puzzle_cells.find(pc => pc.row_index === r && pc.col_index === c);

  return (
    <div className="grid-wrapper w-full" style={{maxWidth:"100%",overflowX:"hidden"}}>
      {/* Column headers */}
      <div className="grid gap-1.5 mb-1.5" style={{gridTemplateColumns:"minmax(0,72px) 1fr 1fr 1fr"}}>
        <div />
        {puzzle.col_labels.map((col, i) => (
          <LabelPopover
            key={i}
            tag={col.tag}
            label={col.label}
            accentClass="text-accent"
            height="h-[72px]"
          />
        ))}
      </div>

      {/* Rows */}
      {puzzle.row_labels.map((row, r) => (
        <div key={r} className="grid gap-1.5 mb-1.5" style={{gridTemplateColumns:"minmax(0,72px) 1fr 1fr 1fr"}}>
          {/* Row header */}
          <LabelPopover
            tag={row.tag}
            label={row.label}
            accentClass="text-accent2"
            height="h-[80px]"
          />

          {/* Cells */}
          {[0, 1, 2].map(c => {
            const cellData    = grid[r][c];
            const isCorrect   = cellData?.correct;
            const isActive    = activeCell?.r === r && activeCell?.c === c;
            const hasHint     = hintShown[r][c];
            const cell        = getCell(r, c);
            const rarity      = cell?.rarity_pct;
            const revealAnswer = revealed && !isCorrect
              ? (cell?.valid_answers?.[0] || null)
              : null;

            return (
              <div
                key={c}
                onClick={() => { selectCell(r, c); focusSearch?.(); }}
                className={`
                  relative overflow-hidden rounded-xl h-[80px] flex flex-col items-center justify-center p-1.5
                  transition-all duration-150 cursor-pointer
                  ${isCorrect
                    ? 'border border-correct bg-correct/5 cursor-default'
                    : isActive
                    ? 'border border-accent bg-surface2 shadow-[0_0_0_1px_var(--accent),inset_0_0_20px_rgba(0,229,255,0.04)]'
                    : hasHint
                    ? 'border border-hint/40 bg-surface hover:bg-surface2 hover:border-borderB'
                    : 'border border-border bg-surface hover:bg-surface2 hover:border-borderB hover:scale-[1.02]'
                  }
                  ${game.gameOver && !isCorrect && !revealAnswer ? 'opacity-45 cursor-default' : ''}
                  ${revealAnswer ? 'border border-accent3/40 bg-accent3/5 cursor-default' : ''}
                `}
              >
                {isCorrect ? (
                  <>
                    <div className="shimmer absolute top-[-50%] left-[-50%] w-[40%] h-[200%] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-[shimmer_2s_ease_0.3s_forwards]" />
                    <div className="text-sm mb-0.5">✓</div>
                    <div className="text-[11px] font-semibold text-correct text-center leading-tight px-1">
                      {cellData.answer.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                    </div>
                    {rarity != null && (
                      <div className="font-mono text-[10px] text-dimText mt-0.5">{rarity}% chose this</div>
                    )}
                  </>
                ) : hasHint ? (
                  <>
                    <div className="text-[9px] font-mono tracking-widest text-hint opacity-50 uppercase mb-1">💡 hint</div>
                    <div className="text-[10px] italic text-hint text-center px-1.5 leading-snug opacity-90">
                      {cell?.hint || 'Think carefully...'}
                    </div>
                  </>
                ) : revealAnswer ? (
                  <>
                    <div className="text-[9px] font-mono tracking-widest text-accent3 opacity-60 uppercase mb-1">answer</div>
                    <div className="text-[10px] font-semibold text-accent3 text-center px-1.5 leading-snug">
                      {revealAnswer.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                    </div>
                  </>
                ) : (
                  <span className="text-xl opacity-10">⚽</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
